import { useMutation } from "@tanstack/react-query";

import { ShowToast } from "../../components/toast";
import { api } from "../api";
import { useApp } from "../app-context";
import { getUser } from "../utils";

const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024;

export class ProfileLogoUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProfileLogoUploadError";
  }
}

interface AppContextShape {
  setUserProfileImg: (url: string | null) => void;
}

function persistProfileImgLocally(url: string | null) {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) return;
  try {
    const userData = JSON.parse(storedUser);
    userData.profileImg = url;
    localStorage.setItem("user", JSON.stringify(userData));
  } catch (err) {
    console.error("Failed to update cached user profile image:", err);
  }
}

export function useProfileLogoUpload() {
  const { setUserProfileImg } = useApp() as AppContextShape;

  const uploadMutation = useMutation<string, Error, File>({
    mutationFn: async (file: File) => {
      const user = getUser();
      if (!user?.id) {
        throw new ProfileLogoUploadError("You must be signed in to upload a logo");
      }
      if (!file.type.startsWith("image/")) {
        throw new ProfileLogoUploadError("Please upload an image file");
      }
      if (file.size > MAX_LOGO_SIZE_BYTES) {
        throw new ProfileLogoUploadError("File size must be less than 5MB");
      }

      const { uploadUrl, accessUrl } = await api.getSignedUrl(user.id, {
        uploadType: "USER_PROFILE",
        mimeType: file.type,
        fileName: file.name,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadResponse.ok) {
        throw new Error(
          `S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
        );
      }

      await api.putUserProfile(user.id, { profileImg: accessUrl });
      persistProfileImgLocally(accessUrl);
      setUserProfileImg(accessUrl);

      return accessUrl;
    },
    onSuccess: () => {
      ShowToast({ type: "success", message: "Logo uploaded successfully" });
    },
    onError: (error) => {
      console.error("Error uploading logo:", error);
      const message =
        error instanceof ProfileLogoUploadError
          ? error.message
          : "Failed to upload logo";
      ShowToast({ type: "error", message });
    },
  });

  const removeMutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      const user = getUser();
      if (!user?.id) {
        throw new ProfileLogoUploadError("You must be signed in to remove a logo");
      }

      await api.putUserProfile(user.id, { profileImg: null });
      persistProfileImgLocally(null);
      setUserProfileImg(null);
    },
    onSuccess: () => {
      ShowToast({ type: "success", message: "Logo removed successfully" });
    },
    onError: (error) => {
      console.error("Error removing logo:", error);
      const message =
        error instanceof ProfileLogoUploadError
          ? error.message
          : "Failed to remove logo";
      ShowToast({ type: "error", message });
    },
  });

  return {
    uploadLogo: uploadMutation.mutateAsync,
    removeLogo: removeMutation.mutateAsync,
    isUploading: uploadMutation.isLoading,
    isRemoving: removeMutation.isLoading,
    isBusy: uploadMutation.isLoading || removeMutation.isLoading,
  };
}
